import { DynamicElementMixin } from "%/dynamic-component-editor/mixins/dynamic-element-mixin";
import { ElementEditMixin } from "%/dynamic-component-editor/mixins/element-edit-mixin";
import useDynamicComponentEditorStore from "%/dynamic-component-editor/stores/dynamic-component-editor";
import DynamicElement from "%/dynamic-component-editor/classes/dynamic-element";
import {
  LayoutElementMixinProduction,
} from "%/dynamic-component-editor/standard-elements/layouts/layout-element-mixin-production";
import {
  ChildTrackingMixin,
} from "%/dynamic-component-editor/standard-elements/layouts/child-tracking-changes-mixin";
import BorderSides from "%/dynamic-component-editor/options/border-sides";
import ClickAction from "%/dynamic-component-editor/options/click-action";
import { mapActions } from "pinia";

export const LayoutElementMixin = {
  props: {
    name: {
      type: String,
      default: "",
      required: false,
    },
  },
  mixins: [
    LayoutElementMixinProduction,
    DynamicElementMixin,
    ElementEditMixin,
    ChildTrackingMixin,
  ],
  elementPanelSettings: {
    displayIcon: "layout",
    elementType: "layout",
  },
  optionBundles: [],
  options: [
    BorderSides,
    ClickAction,
  ],
  computed: {
    isTemplatedComponent() {
      return useDynamicComponentEditorStore().isElementTemplatedComponent(this.identifier);
    },
    isEmpty() {
      return (DynamicElement.get(this.identifier)?.childElementIds?.length ?? 0) === 0;
    },
    isRootLayout() {
      return !DynamicElement.get(this.identifier)?.parentElementId;
    },
    myEventListeners() {
      return {
        click: event => {
          event.stopPropagation();
          this.internalHandleOnEdit(event);
        },
        mouseover: event => {
          event.stopPropagation();
          this.internalMouseOverHandler();
        },
        mouseleave: event => {
          event.stopPropagation();
          this.internalMouseLeaveHandler();
        },
        dragstart: event => {
          if (this.breakpoint) {
            return;
          }
          event.stopPropagation();
          this.internalHandleDragStart(event);
        },
        dragend: event => {
          if (this.breakpoint) {
            return;
          }
          event.stopPropagation();
          this.internalHandleDragEnd(event);
        },
        dragover: event => {
          if (this.breakpoint) {
            return;
          }
          event.stopPropagation();
          this.internalHandleDragOver(event);
        },
        drop: event => {
          if (this.breakpoint) {
            return;
          }
          event.stopPropagation();
          this.internalHandleDrop(event);
        },
        dragleave: event => {
          if (this.breakpoint) {
            return;
          }
          event.stopPropagation();
          this.internalHandleDragLeave(event);
        },
        contextmenu: event => {
          if (this.editMode) {
            event.stopPropagation();
            event.preventDefault();
            this.openContextualMenuForBuilder(event);
          }
        },
      };
    },
    contextMenuAttrs() {
      const configuration = { ...this.dynamicComponentInstance.getElement(this.identifier) };
      return {
        elementId: this.identifier,
        style: this.contextMenuFixedPositionStyle,
        canCopy: true,
        canPaste: true,
        isDeletable: this.deletable(this.identifier, configuration) && !this.isRootLayout,
        isDuplicatable: this.duplicatable(this.identifier, configuration) && !this.isRootLayout,
        isEditable: this.editable(this.identifier, configuration),
        canWrapInHorizontalContainer: !this.isRootLayout && this.wrappable(this.identifier, configuration),
        canWrapInVerticalContainer: !this.isRootLayout && this.wrappable(this.identifier, configuration),
        canWrapSelectionInContainer: this.canWrapCurrentSelection(),
        canCreateCustomComponent: !this.isDynamicAppComponent && this.canCreateTemplateFrom(this.identifier, configuration),
        isTemplatedComponent: !this.isDynamicAppComponent && this.isTemplatedComponent,
        name: this.name,
        themeId: 0,
        // Explicit, not "inherit" — this component is bound to the canvas's live toggle.
        themeMode: "unset",
      };
    },
    contextMenuEvents() {
      return {
        close: () => this.setShowContextualMenuForIdInBuilder(null),
        delete: this.internalHandleOnDelete,
        duplicate: this.internalHandleDuplicate,
        edit: this.internalHandleOnEdit,
        "wrap-in-horizontal-container": this.internalWrapInHorizontalContainer,
        "wrap-in-vertical-container": this.internalWrapInVerticalContainer,
        "wrap-selection-in-vertical-container": this.internalWrapSelectionInVerticalContainer,
        "wrap-selection-in-horizontal-container": this.internalWrapSelectionInHorizontalContainer,
        "create-custom-component": this.internalCreateCustomComponent,
        "create-new-template-from-here": this.internalCreateDynamicComponentFromIdentifier,
        copy: this.internalHandleCopy,
        paste: this.internalHandlePaste,
      };
    },
  },
  methods: {
    ...mapActions(useDynamicComponentEditorStore, {
      "deletable": "isDeletable",
      "editable": "isEditable",
      "duplicatable": "isDuplicatable",
      "wrappable": "canWrap",
      "canCreateTemplateFrom": "canCreateCustomComponent",
      "canWrapCurrentSelection": "canWrapCurrentSelection",
    }),
  },
};
